import { motion, useInView, useScroll } from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type StoryStep = {
	key: string;
	title: string;
	description?: string;
	details?: { label: string; text: string }[];
};

/**
 * "How it works" told in chapters: text scrolls on the left while the visual
 * stays pinned on the right and follows the chapter in the middle of the
 * screen. A rail fills with the scroll. Below `lg`, every chapter carries its
 * own visual inline.
 */
export function StickyStory({
	steps,
	renderVisual,
	className,
}: {
	steps: StoryStep[];
	renderVisual: (index: number) => React.ReactNode;
	className?: string;
}) {
	const [active, setActive] = useState(0);
	const listRef = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({
		target: listRef,
		offset: ["start center", "end center"],
	});

	return (
		<div
			className={cn(
				"grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20",
				className,
			)}
		>
			<div ref={listRef} className="relative">
				<span
					aria-hidden="true"
					className="absolute top-2 bottom-2 left-[0.6875rem] hidden w-px bg-border lg:block"
				/>
				<motion.span
					aria-hidden="true"
					className="absolute top-2 bottom-2 left-[0.6875rem] hidden w-px origin-top bg-signal lg:block"
					style={{ scaleY: scrollYProgress }}
				/>
				<ol>
					{steps.map((step, index) => (
						<Chapter
							key={step.key}
							step={step}
							index={index}
							active={index === active}
							onEnter={() => setActive(index)}
							visual={renderVisual(index)}
						/>
					))}
				</ol>
			</div>
			<div className="hidden lg:block">
				<div className="sticky top-[calc(50vh-15rem)]">
					{renderVisual(active)}
				</div>
			</div>
		</div>
	);
}

function Chapter({
	step,
	index,
	active,
	onEnter,
	visual,
}: {
	step: StoryStep;
	index: number;
	active: boolean;
	onEnter: () => void;
	visual: React.ReactNode;
}) {
	const ref = useRef<HTMLLIElement>(null);
	const inView = useInView(ref, { margin: "-45% 0px -45% 0px" });
	const onEnterRef = useRef(onEnter);
	onEnterRef.current = onEnter;

	// Only reacts to the chapter entering the middle band of the screen.
	useEffect(() => {
		if (inView) onEnterRef.current();
	}, [inView]);

	return (
		<li
			ref={ref}
			className="relative pb-14 last:pb-0 lg:flex lg:min-h-[64vh] lg:items-center lg:pb-0 lg:pl-14"
		>
			<div className="relative max-w-md">
				<span
					aria-hidden="true"
					className={cn(
						"absolute top-1 -left-14 hidden size-[1.375rem] items-center justify-center rounded-full border bg-background font-mono text-[10px] transition-colors duration-500 lg:flex",
						active && "border-signal bg-signal text-signal-foreground",
					)}
				>
					{index + 1}
				</span>
				<p className="mb-3 font-mono text-xs text-signal-ink lg:hidden">
					{String(index + 1).padStart(2, "0")}
				</p>
				<h3
					className={cn(
						"text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance transition-colors duration-500 md:text-3xl",
						active ? "text-foreground" : "lg:text-muted-foreground",
					)}
				>
					{step.title}
				</h3>
				{step.description ? (
					<p className="mt-4 text-lg text-pretty text-muted-foreground">
						{step.description}
					</p>
				) : null}
				{step.details?.length ? (
					<dl className="mt-6 space-y-4 border-l-2 border-signal/60 pl-5 text-sm">
						{step.details.map((detail) => (
							<div key={detail.label}>
								<dt className="font-mono text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
									{detail.label}
								</dt>
								<dd className="mt-1 text-base text-pretty">{detail.text}</dd>
							</div>
						))}
					</dl>
				) : null}
				<div className="mt-8 lg:hidden">{visual}</div>
			</div>
		</li>
	);
}
